/* =========================================================================
 * bowlWork.js - what a bowl team row OFFERS, and how its worklist orders read.
 * Pure. No React, no fetch.
 *
 * Brian, 2026-10-26: "in the bowl eligibility tab - there is no way to control
 * any of it - like send an email or assign a rep when youve got 3 listed."
 *
 * ⭐ THE SERVER DECIDES THE ROW; THIS ONLY DRAWS IT. Every board team arrives
 * with `work` from the backend's teamWork(), which is the SAME function the
 * worklist orders are built from. So this file never re-derives "what does this
 * team need" - it maps the server's answer onto controls, and it says which of
 * them this viewer may press. Every press is re-decided by the server anyway:
 * hiding a control is a courtesy, not the gate.
 *
 * "Phil" means Philip + Madison and "Zach" means Zach + Alexis: the desk the
 * server returns already carries the pair, so the partner sees the same
 * controls as the lead.
 * ========================================================================= */
import { pricingDeepLink } from './championToClose.js'

const S = (v) => (v == null ? '' : String(v)).trim()

export const STEPS = ['email', 'art', 'quote', 'win']
export const STEP_LABEL = { email: 'Email', art: 'Art', quote: 'Quote', win: 'Win' }

/** The key every action on a bowl team is filed under (mirrors the backend). */
export function bowlKey(season, team) {
  return `fbs-bowl:${Number(season) || ''}:${S(team)}`
}

/** Is this viewer on the team's desk (the lead or the standing-pair partner)? */
export function onDesk(work, viewerName, viewerId = '') {
  const v = S(viewerName).toLowerCase()
  if (!v) return false
  if (work?.confirmedTeam) return !!viewerId && Array.isArray(work.actionMemberIds) && work.actionMemberIds.includes(viewerId)
  const a = work?.accountable
  if (!a) return false
  return [a.name, a.short, a.pairedWith].some((n) => S(n).toLowerCase() === v)
}

/**
 * The controls one row offers this viewer, in the order they render.
 *
 * Each is `{ key, label, kind, enabled, why }`. A control that cannot be
 * pressed by THIS viewer still renders with the reason it is not theirs, so a
 * rep never stares at a row wondering why nothing is offered - and never
 * gets a button the server will refuse.
 */
export function rowControls(work, { isExec = false, viewerIsExec = isExec, canPick = isExec, viewerName = '', viewerId = '', setupHref = '/governing-bodies' } = {}) {
  if (!work) return { controls: [], fixes: [], status: 'unreadable' }
  const mine = isExec || onDesk(work, viewerName, viewerId)
  const whose = deskLine(work)
  const notMine = `This team is on ${whose}'s desk.`
  const fixes = []

  if (work.deskState === 'needs_pick') {
    fixes.push({ key: 'pick_rep', kind: 'pick', label: 'Assign team', enabled: canPick,
      why: canPick ? `${work.listed.length} reps are listed; review every person assigned to this school.`
        : viewerIsExec ? 'Desk selection is paused until assignment setup, current history and permission checks are ready.'
        : `${work.listed.length} reps are listed. An authorized teammate assigns the school team; it is on their worklist.` })
  } else if (work.deskState === 'no_desk') {
    fixes.push({ key: 'assign_desk', kind: 'link', to: setupHref, label: 'Assign a desk', enabled: true,
      why: 'Nobody covers this school. The desk is set on Governing Bodies.' })
  }
  if (!work.account) {
    /* A pinned proposal turns "pick the company" into "confirm this one"
     * (2026-11-01: all 40 became-eligible rows were dead ends without it). */
    const p = work.accountProposal
    fixes.push({ key: 'confirm_account', kind: 'account', label: p ? `Confirm: ${p.name}` : 'Confirm the school account', enabled: isExec,
      proposal: p || null,
      why: isExec ? (p ? `Proposed: ${p.basis || p.name} One tap confirms it; Email and Win open after.`
        : 'Pick the HubSpot company this team is. Email and Win hang off it.')
        : viewerIsExec ? 'School confirmation is paused until assignment setup, current history and permission checks are ready.'
        : 'No school account is confirmed yet. An exec confirms it; it is on their worklist.' })
  } else if (work.contactState === 'none') {
    fixes.push({ key: 'find_contact', kind: 'contact', label: 'Add the contact', enabled: mine && !work.blockedActions?.includes('contact'),
      why: work.blockedActions?.includes('contact') ? 'This operation requires an explicitly chosen primary desk.' : mine ? `${work.account.companyName} has no contact with an email. Add one.` : notMine })
  }

  const needAccount = !work.account
  const controls = STEPS.map((step) => {
    const d = work.done?.[step]
    const base = { key: step, kind: step, label: STEP_LABEL[step], done: d || null, next: work.nextStep === step }
    if (work.blockedActions?.includes(step)) return {...base,enabled:false,why:'The school team is confirmed. This operation requires an explicitly chosen primary desk.'}
    if (!mine) return { ...base, enabled: false, why: notMine }
    if (step === 'email' && needAccount) return { ...base, enabled: false, why: 'Confirm the school account first.' }
    if (step === 'win' && needAccount) return { ...base, enabled: false, why: 'A win is recorded against the school account.' }
    return { ...base, enabled: true, why: '' }
  })
  return { controls, fixes, mine, status: work.complete ? 'won' : 'open' }
}

/** The reason prefilled when the exec confirms a pinned company (<= 300 chars, the input's cap). */
export function pinnedReason(basis) {
  return `Pinned match confirmed: ${S(basis)}`.slice(0, 300)
}

/** "Philip Davanzo III + Madison Gill", or a plain statement of who decides. */
export function deskLine(work) {
  if (work?.members?.length) return [...new Set(work.members.map(m => m.name).filter(Boolean))].join(' + ')
  const a = work?.accountable
  if (!a) return work?.deskState === 'no_desk' ? 'nobody' : 'an exec pick'
  return a.pairedWith ? `${a.name} + ${a.pairedWith}` : S(a.name || a.short)
}

/** One line of progress for the card: what is done, and the next step. */
export function progressLine(work) {
  if (!work) return ''
  const done = STEPS.filter((s) => work.done?.[s]).map((s) => `${s === 'email' && (work.done[s].auto || work.done[s].detail?.draftId) ? 'Email draft' : STEP_LABEL[s]} ${S(work.done[s].at).slice(0, 10)}`.trim())
  if (work.complete) return `Won: ${work.done.win?.note || 'recorded'}.`
  const next = work.nextStep ? `Next: ${STEP_LABEL[work.nextStep]}.` : ''
  return [done.length ? `Done: ${done.join(', ')}.` : 'No work recorded yet.', next].filter(Boolean).join(' ')
}

/** The pricing engine, pre-filled, exactly as the championship board opens it. */
export function quoteHref(season, team, work) {
  return pricingDeepLink({
    championshipId: bowlKey(season, team),
    champion: { school: work?.account?.companyName || S(team) },
    championship: { sport: 'Football', level: 'College' },
  })
}

/** Artwork Studio's new-job flow, with the job titled for this team. */
export function artHref(team) {
  return `/artwork-studio?${new URLSearchParams({ title: `${S(team)} bowl ring` }).toString()}`
}

/** Early outreach is allowed before eligibility. The default asserts no title,
 * eligibility, date, attached artwork or prior relationship. */
export function emailTemplate(team, work, repName = '') {
  const school = work?.account?.companyName || S(team)
  const sender = S(repName)
  return {
    subject: `${school} - a ring that feels like your team`,
    body: school && sender ? `A ring should bring ${school} back to the people and moments that made this team its own. The design starts with what matters to you.\n\n`
      + `What would you want it to capture?\n\n${sender}\nSouthern Recognition` : '',
  }
}

/** The client-side twin of the server's copy guard, so the rep sees it first. */
export function copyProblem({ subject = '', body = '' } = {}) {
  if (/\bchampions?\b|\bchampionship\b|\bnational title\b/i.test(`${subject}\n${body}`)) {
    return 'This early-outreach draft must not assert a championship title. Remove the title claim before drafting.'
  }
  if (!S(subject) || !S(body)) return 'A draft needs a subject and a body.'
  return ''
}

/** Validate a step note before it is sent (mirrors the server). */
export function stepProblem(step, note) {
  if (S(note).length >= 5) return ''
  return step === 'win' ? 'Name the order (ClickUp order or PO) so the win can be found again.'
    : 'Say what went out, in a sentence.'
}

/** Where a worklist order takes you: the board, opened on that team. */
export function orderHref(order) {
  return `/bowl-eligibility?${new URLSearchParams({ team: S(order?.team), do: S(order?.kind === 'step' ? order.step : order?.kind) }).toString()}`
}

/** Tapping a team's name opens what it needs next: the first fix, else the
 *  next selling step. The same door a worklist order uses. */
export function teamHref(team, work) {
  const first = work?.fixes?.[0]?.kind || (work?.nextStep || '')
  const q = { team: S(team) }
  if (first) q.do = first
  return `/bowl-eligibility?${new URLSearchParams(q).toString()}`
}

/** The sentence that proves the worklist section is not dropping orders. */
export function reconcileLine(resp, whose) {
  if (!resp) return ''
  const n = (x) => Number(x || 0).toLocaleString()
  return `${n(resp.total)} bowl ${resp.total === 1 ? 'order' : 'orders'} on the board = `
    + `${n(resp.mine)} for ${whose} + ${n(resp.others)} with somebody else.`
}
